import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Route, PieChart as PieIcon, TrendingUp, Users } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { api } from "../../lib/api";

const PALETTE = ["#00E5FF", "#FF7A00", "#00B8A9", "#7C5CFF", "#F5C542", "#FF4D6D"];
const CYAN = "#00E5FF";
const ORANGE = "#FF7A00";

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-popover/95 backdrop-blur px-3 py-2 text-xs shadow-xl">
      {label && <div className="font-mono-x tracking-widest text-[10px] text-muted-foreground mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="capitalize text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-semibold text-foreground">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const Card = ({ icon: Icon, title, testId, children, className = "" }) => (
  <div data-testid={testId} className={`rounded-2xl border border-border bg-card/40 p-5 ${className}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-primary" />
      <h3 className="font-heading font-bold text-sm tracking-tight">{title}</h3>
    </div>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (err) return <div data-testid="analytics-error" className="text-sm text-muted-foreground">Couldn't load analytics.</div>;
  if (!data) return <div data-testid="analytics-loading" className="text-sm text-muted-foreground animate-pulse">Loading analytics…</div>;

  const { totals, per_week, top_lanes, service_breakdown, status_breakdown, applications_by_role = [] } = data;
  const weekTotalQuotes = per_week.reduce((a, w) => a + w.quotes, 0);
  const maxLane = Math.max(1, ...top_lanes.map((l) => l.count));

  const kpis = [
    { label: "Quote Requests", val: totals.quotes, sub: `${totals.quotes_handled} handled`, tid: "kpi-quotes" },
    { label: "Contact Leads", val: totals.leads, sub: `${totals.leads_handled} handled`, tid: "kpi-leads" },
    { label: "Applications", val: totals.applications ?? 0, sub: `${totals.applications_handled ?? 0} handled`, tid: "kpi-applications" },
    { label: "Active Shipments", val: totals.shipments, sub: "in the network", tid: "kpi-shipments" },
    { label: "Published Posts", val: totals.published_posts, sub: `${totals.subscribers} subscribers`, tid: "kpi-posts" },
  ];

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((k, i) => (
          <motion.div key={k.label} data-testid={k.tid}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="rounded-2xl border border-border bg-card/40 p-6">
            <div className="font-heading font-extrabold text-4xl text-primary">{k.val}</div>
            <div className="mt-1 text-sm text-foreground">{k.label}</div>
            <div className="text-xs text-muted-foreground">{k.sub}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card icon={BarChart3} title="Quotes & leads · last 8 weeks" testId="chart-per-week" className="lg:col-span-2">
          <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-primary" /> {weekTotalQuotes} quotes in the last 8 weeks
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={per_week} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="quotes" fill={CYAN} radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Bar dataKey="leads" fill={ORANGE} radius={[4, 4, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card icon={PieIcon} title="Service mix" testId="chart-service-mix">
          {service_breakdown.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">No quote data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={service_breakdown} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={3} stroke="none">
                  {service_breakdown.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip content={<ChartTip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card icon={Route} title="Top lanes" testId="chart-top-lanes" className="lg:col-span-2">
          {top_lanes.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No lane data yet.</div>
          ) : (
            <div className="space-y-3">
              {top_lanes.map((l, i) => (
                <div key={l.lane} data-testid={`lane-row-${i}`} className="flex items-center gap-3">
                  <div className="w-40 shrink-0 text-sm font-medium truncate">{l.lane}</div>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(l.count / maxLane) * 100}%` }}
                      transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
                      className="h-full rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                  </div>
                  <div className="w-6 text-right text-sm font-semibold text-primary">{l.count}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card icon={PieIcon} title="Shipment status" testId="chart-status" className="lg:col-span-1">
          {status_breakdown.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No shipments yet.</div>
          ) : (
            <div className="space-y-2.5">
              {status_breakdown.map((s, i) => (
                <div key={s.name} data-testid={`status-row-${i}`} className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                  <span className="text-muted-foreground">{s.name}</span>
                  <span className="ml-auto font-semibold">{s.value}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card icon={Users} title="Applicants by role" testId="chart-applicants">
        {applications_by_role.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">No applications yet.</div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-4">
            {applications_by_role.map((s, i) => (
              <div key={s.name} data-testid={`role-row-${i}`} className="flex items-center gap-3 rounded-xl border border-border bg-card/40 p-4">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                <span className="text-sm text-muted-foreground truncate">{s.name}</span>
                <span className="ml-auto font-heading font-extrabold text-2xl text-primary">{s.value}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
